import { useCallback, useMemo } from 'react';
import {
	ToothPathology,
	ToothPathologyDecayValues,
	ToothSurface,
} from '../../../../../graphql/graphql';
import { SurfaceOperation } from '../../../Enums/SurfaceOperation.enum';
import { handleSurfacesOperation } from '../../../Helpers/surfaces/toothSurfaces.helper';

export default function useToothPathologyDecays({
	pathology,
	setPathology,
}: {
	pathology?: ToothPathology;
	setPathology: (pathology: ToothPathology) => void;
}) {
	const getSurfaces = useCallback(
		(index: number) => {
			return pathology?.decays?.[index]?.surfaces ?? [];
		},
		[pathology, setPathology],
	);

	const setSurfaces = useCallback(
		(index: number, surfaces: ToothSurface[], operation: SurfaceOperation) => {
			const nextDecays = [...(pathology?.decays ?? [])];
			const currentDecay = nextDecays[index] ?? {};

			nextDecays[index] = {
				...currentDecay,
				surfaces: handleSurfacesOperation(
					currentDecay?.surfaces ?? [],
					surfaces,
					operation,
				),
			};

			setPathology({
				...pathology,
				decays: nextDecays,
			});
		},
		[pathology, setPathology],
	);

	const getValues = useCallback(
		(index: number) => {
			return pathology?.decays?.[index]?.values ?? {};
		},
		[pathology, setPathology],
	);

	const setValues = useCallback(
		(index: number, values: ToothPathologyDecayValues) => {
			const nextDecays = [...(pathology?.decays ?? [])];
			const currentDecay = nextDecays[index] ?? {};

			nextDecays[index] = {
				...currentDecay,
				surfaces: currentDecay?.surfaces ?? [],
				values,
			};

			setPathology({
				...pathology,
				decays: nextDecays,
			});
		},
		[pathology, setPathology],
	);

	const reset = useCallback(
		(index: number) => {
			const nextDecays = [...(pathology?.decays ?? [])];

			nextDecays.splice(index, 1);

			setPathology({
				...pathology,
				decays: nextDecays,
			});
		},
		[pathology, setPathology],
	);

	return useMemo(
		() => ({
			decays: {
				getSurfaces,
				setSurfaces,
				getValues,
				setValues,
				reset,
			},
		}),
		[pathology],
	);
}
